import { CustomColorThemesProvider } from "./contexts/CustomColorThemesContext";
import { DeepLinkContextProvider } from "./contexts/DeepLinkContext";
import { StaticSiteDataProvider } from "./contexts/StaticSiteDataContext";
import { TrackContextProvider } from "./contexts/TrackContext";
import API from "./core/api";
import { deepCamelCaseKeys } from "./core/api/drf";
import { SentryBoundary } from "./core/components/SentryBoundary";
import { trackPageLoad } from "./core/instrumentation";
import configureStore from "./core/store";
import RefManager from "./ref-monitor/RefManager";
import RefMonitorProvider from "./ref-monitor/components/RefMonitorProvider";
import { loadSiteData } from "./website/actions";
import LoginWrapper from "./website/components/LoginWrapper";
import Website from "./website/components/Website";
import HolidayWebsite from "./website/components/holiday-website/HolidayWebsite";
import OnlineInvitationSite from "./website/components/online-invitation/OnlineInvitationSite";
import createRouter, { createRoutes } from "./website/router";
import unauthenticatedRoutes from "./website/routes";
import { selectSite, filterPage } from "./website/selectors";
import config from "config";
import React from "react";
import { render } from "react-dom";
import ReactGA from "react-ga";
import { Provider } from "react-redux";
import { RouterProvider } from "react-router5";
import smoothScroll from "smoothscroll-polyfill";
import { v4 as uuidV4 } from "uuid";

smoothScroll.polyfill();

ReactGA.initialize(config.get("guestGATrackingID"));

const siteData = deepCamelCaseKeys(window.__DATA__, ["themeDataOverrides"]);

const {
  accommodations,
  attendants,
  comments,
  customColorThemes,
  events,
  imageUploads,
  pages,
  photos,
  registries,
  videos,
  ...rest
} = siteData;

const data = {
  accommodations,
  attendants,
  comments,
  events,
  "image-uploads": imageUploads,
  pages,
  photos,
  registries,
  sites: [rest],
  videos,
};

const baseUrl = window.__BASE_URL__;
const guestAuthenticated = window.__GUEST_AUTHENTICATED__ === "True";

let routes;
let defaultRoute;

if (guestAuthenticated) {
  routes = createRoutes(pages.filter((page) => filterPage(page, data)));
  defaultRoute = "home";
} else {
  routes = unauthenticatedRoutes;
  defaultRoute = "login";
}

const deepLinkContactId = window.__ADDRESSBOOK_CONTACT_ID__;

API.setDomain(siteData.domain);

const router = createRouter(routes, baseUrl, defaultRoute);
const store = configureStore(router);

store.dispatch(loadSiteData(data));

const refManager = new RefManager();

const state = store.getState();

const site = selectSite(state.resources.sites);

let SiteComponent;

if (site.isWedding) {
  SiteComponent = Website;
} else if (site.isHoliday) {
  SiteComponent = HolidayWebsite;
} else {
  SiteComponent = OnlineInvitationSite;
}

store.dispatch({
  payload: window.__THEME_DATA__,
  type: "FETCH_THEME_FULFILLED",
});

router.start((error, state) => {
  if (error) {
    console.error(error);
  }

  let guestId = localStorage.getItem("mintedWebsitesGuestId");

  if (!guestId) {
    guestId = uuidV4();

    localStorage.setItem("mintedWebsitesGuestId", guestId);
  }

  trackPageLoad(
    site.isDemoSite ? "site_demo" : "site_guest_experience",
    site,
    guestId,
  );

  render(
    <SentryBoundary>
      <Provider store={store}>
        <RouterProvider router={router}>
          <RefMonitorProvider refManager={refManager}>
            <StaticSiteDataProvider site={site}>
              <TrackContextProvider site={site}>
                <CustomColorThemesProvider
                  initialCustomColorThemes={customColorThemes || []}
                >
                  <LoginWrapper>
                    <DeepLinkContextProvider
                      addressbookContactId={deepLinkContactId}
                    >
                      <SiteComponent />
                    </DeepLinkContextProvider>
                  </LoginWrapper>
                </CustomColorThemesProvider>
              </TrackContextProvider>
            </StaticSiteDataProvider>
          </RefMonitorProvider>
        </RouterProvider>
      </Provider>
    </SentryBoundary>,
    document.getElementById("root"),
  );
});
